package com.exura.features.plandefinition.data.mappers.plandefinition

import com.exura.features.firebase.data.mappers.SerializableMappers
import com.exura.features.plandefinition.data.remote.dto.series.*
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.series.*
import dev.gitlive.firebase.firestore.CollectionReference

/**
 * Base interface for all series mappers.
 * Extends SerializableMappers for Firebase storage compatibility.
 *
 * @param T The specific BaseSeries implementation type
 * @param D The specific BaseSeriesDto implementation type
 */
interface BaseSeriesMappers<T : BaseSeries<out BasePlanElement>, D : BaseSeriesDto> : SerializableMappers<T, D>

/**
 * Generic function for converting a BaseSeries domain model to its corresponding DTO.
 * This should be used when the specific series type is unknown at compile time.
 *
 * @param model The domain model to convert
 * @return The corresponding DTO
 */
fun BaseSeries<out BasePlanElement>.toDto(): BaseSeriesDto {
    // This implementation requires specific mappers for each concrete series type
    return when (this) {
        // Add implementations for concrete series types as needed
         is SimpleInstructionSeries -> SimpleInstructionSeriesMappers().toDto(this)
         is SimplePersistentSeries -> SimplePersistentSeriesMappers().toDto(this)
         is SimpleQuestionSeries -> SimpleQuestionSeriesMappers().toDto(this)
         is SimpleFloatingSeries -> SimpleFloatingSeriesMappers().toDto(this)
        else -> throw IllegalArgumentException("Unknown series type: ${this::class.simpleName}")
    }
}

/**
 * Generic function for converting a BaseSeriesDto to its corresponding domain model.
 * This should be used when the specific DTO type is unknown at compile time.
 *
 * @param collection The Firestore collection reference
 * @param id The document ID
 * @return The corresponding domain model
 */
fun BaseSeriesDto.toDomain(collection: CollectionReference? = null, id: String? = null): BaseSeries<out BasePlanElement> {
    // This implementation requires specific mappers for each concrete DTO type
    return when (this) {
        // Add implementations for concrete DTO types as needed
         is SimpleInstructionSeriesDto -> SimpleInstructionSeriesMappers().toDomain(this, collection, id)
         is SimplePersistentSeriesDto -> SimplePersistentSeriesMappers().toDomain(this, collection, id)
         is SimpleQuestionSeriesDto -> SimpleQuestionSeriesMappers().toDomain(this, collection, id)
         is SimpleFloatingSeriesDto -> SimpleFloatingSeriesMappers().toDomain(this, collection, id)
        else -> throw IllegalArgumentException("Unknown series DTO type: ${this::class.simpleName}")
    }
}
